/**
 * The phone's half of pairing a television.
 *
 * Reached two ways, and both matter: by scanning the QR on the TV, which fills the code in
 * from the query string, or by typing the six characters shown beneath it. The typed path
 * is not a fallback for old phones — it is what works when the TV is across the room, the
 * camera will not focus, or the QR is on a screen too bright to read.
 *
 * ## This screen is the security boundary
 *
 * Everything before it is a television asking to be let in, which anyone can do. This is
 * the step that carries household authority, so it is deliberately explicit: it names the
 * television, it makes the person choose which profile the TV starts on, and it never
 * proceeds on a tap alone. A screen that auto-approved whatever code was in the URL would
 * turn a scanned QR — a thing people scan without reading — into a silent grant.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PairingPendingDto, ProfileDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { ErrorBox, Spinner } from '../components/ui';
import { Avatar } from '../components/Avatar';
import { PAIRING_CODE_LENGTH } from '@filmodasi/shared';

type Phase = 'entering' | 'confirming' | 'approved';

export function PairApprovePage() {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('code') ?? '').toUpperCase());
  const [pending, setPending] = useState<PairingPendingDto | null>(null);
  const [profiles, setProfiles] = useState<ProfileDto[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('entering');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<ProfileDto[]>('/api/profiles', { withoutProfile: true })
      .then((list) => {
        setProfiles(list);
        // Pre-selected but still shown: choosing a profile is the point of the screen,
        // and a household with one profile should not have to make a choice at all.
        if (list.length === 1) setChosen(list[0]!.id);
      })
      .catch(() => setProfiles([]));
  }, []);

  const lookup = useCallback(async (raw: string) => {
    const value = raw.trim().toUpperCase();
    if (value.length !== PAIRING_CODE_LENGTH) return;

    setBusy(true);
    setError(null);
    try {
      const found = await api<PairingPendingDto>(`/api/devices/pair/${value}`, {
        withoutProfile: true,
      });
      setPending(found);
      setPhase('confirming');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kod bulunamadı');
    } finally {
      setBusy(false);
    }
  }, []);

  // A code that arrived by QR is looked up immediately — but only looked up. What it
  // shows is a confirmation screen, never an approval.
  useEffect(() => {
    const fromQr = params.get('code');
    if (fromQr) void lookup(fromQr);
  }, [params, lookup]);

  const approve = async () => {
    if (!pending || !chosen) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/devices/pair/approve', {
        method: 'POST',
        withoutProfile: true,
        body: { code: pending.code, profileId: chosen },
      });
      setPhase('approved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onaylanamadı');
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'approved') {
    return (
      <main className="pair">
        <h1>Televizyon bağlandı</h1>
        <p className="pair__hint">
          {pending?.tvName ?? 'Televizyon'} artık bu hesapta. Ekranda birkaç saniye içinde
          açılır.
        </p>
      </main>
    );
  }

  return (
    <main className="pair">
      <h1>Televizyonu bağla</h1>

      {phase === 'entering' && (
        <>
          <p className="pair__hint">Televizyonun ekranındaki altı karakteri girin.</p>
          <form
            className="paircode"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup(code);
            }}
          >
            <input
              className="paircode__input"
              value={code}
              autoFocus
              // `characters` rather than `words`: this is a code, and an autocapitalised
              // first letter with lowercase rest is the most common way to mistype it.
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={PAIRING_CODE_LENGTH}
              placeholder="ABC123"
              aria-label="Eşleştirme kodu"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <button
              type="submit"
              className="pair__go"
              disabled={busy || code.trim().length !== PAIRING_CODE_LENGTH}
            >
              Devam
            </button>
          </form>
        </>
      )}

      {phase === 'confirming' && pending && (
        <>
          <p>
            <strong>{pending.tvName ?? 'Televizyon'}</strong>
            {pending.tvPlatform ? <span className="pair__hint"> · {pending.tvPlatform}</span> : null}
          </p>
          <p className="pair__hint">Bu televizyon hangi profille açılsın?</p>

          <ul className="pairprofiles">
            {profiles.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`pairprofile${chosen === p.id ? ' is-chosen' : ''}`}
                  onClick={() => setChosen(p.id)}
                >
                  <Avatar profile={p} size={40} />
                  <span>{p.name}</span>
                </button>
              </li>
            ))}
          </ul>

          <button type="button" className="pair__approve" disabled={busy || !chosen} onClick={() => void approve()}>
            Onayla ve bağla
          </button>
          <button
            type="button"
            className="link"
            onClick={() => {
              setPhase('entering');
              setPending(null);
            }}
          >
            Vazgeç
          </button>
        </>
      )}

      {busy && <Spinner />}
      {error && <ErrorBox message={error} />}
    </main>
  );
}

export default PairApprovePage;
